export const cdp = {
	tabs: {
		dashboard: "Overview",
		customers: "Customer List",
		rfmAnalysis: "Customer behavior",
		segments: "Segments",
	},
	common: {
		loading: "Loading...",
	},
	header: {
		title: "CRM Panel",
		subtitle:
			"Get to know, track and activate your customer base, turning data into campaigns and sales",
	},
	rfm: {
		labels: {
			champions: "Champions",
			loyal: "Loyal",
			highPotential: "High Potential",
			promising: "Promising",
			recent: "Recent",
			attention: "Attention",
			canNotLose: "Can't Lose Them",
			risk: "At Risk",
			closeToHibernation: "About to Hibernate",
			hibernating: "Hibernating",
			lost: "Lost",
		},
		descriptions: {
			champions: "Bought recently, buy often and spend the most",
			loyal: "Come back often and bought recently",
			highPotential: "Bought recently with good spend — almost loyal",
			promising: "Recent purchase with good frequency and spend signals",
			recent: "Bought recently, but still with few visits",
			attention: "Average recency — need a nudge to come back",
			canNotLose: "Used to spend a lot, but haven't bought in a long time",
			risk: "Bought well before, but have been cooling off for a while",
			closeToHibernation: "Low recency and frequency — nearly inactive",
			hibernating: "Old purchases, with low frequency and spend",
			lost: "Bought little, spent little and never came back",
		},
	},
	dashboard: {
		title: "CDP Dashboard",
		subtitle: "Track your customers, relationship metrics and intelligent segmentation",
		filters: {
			period: {
				label: "Period",
				options: {
					last7Days: "Last 7 days",
					last15Days: "Last 15 days",
					last30Days: "Last 30 days",
					last60Days: "Last 60 days",
					last90Days: "Last 90 days",
				},
			},
			groupBy: {
				label: "Group by",
				options: {
					week: "Week",
					month: "Month",
				},
			},
			establishments: {
				label: "Establishments",
				all: "All",
			},
			apply: "Filter",
		},
		advanced: {
			top10Title: "Top 10 Customers by Value",
			noClients: "No customers found",
			visitsPerMonth: "visits/month",
			viewTop100: "View top 100 customers",
			sectionTitle: "Advanced Analytics",
			sectionSubtitle: "Detailed view of customer value",
			rankingTitle: "Top {{count}} Customers by Value",
			viewMore: "View more",
			viewLess: "View less",
		},
		alerts: {
			title: "Alerts and Risks",
			subtitle: "Attention indicators for your customer base",
			cards: {
				inactive: {
					title: "Inactive Customers",
					label: "Inactive for 90+ days",
					tooltip: "Considers the last 90 days from today.",
					ofBase: "of the base",
					anomalyTooltip:
						"The percentage of inactive customers exceeds 100% of the active base. This may indicate inconsistent data — customers might be counted across multiple venues, or the base total may not include all historical customers.",
				},
				balance: {
					title: "Available Credits",
					tooltip:
						"Balance from top-ups that customers loaded and have not yet spent (excludes refunded or expired top-ups). It is money sitting idle at the venue — a good reason to bring these customers back.",
					clients: "Customers",
					percentOfBase: "% of the base",
					averagePerClient: "Average/customer",
				},
				churn: {
					title: "Customers at Churn Risk",
				},
			},
		},
		demographics: {
			title: "Demographic Analysis",
			subtitle: "Demographic profile of your customer base",
			genderTitle: "Distribution by Gender",
			ageTitle: "Distribution by Age Range",
			ageMissingBirthDate:
				"⚠ {{percentage}}% of customers have no birth date on file and are excluded",
			genders: {
				male: "Male",
				female: "Female",
				other: "Other",
				notInformed: "Not informed",
			},
		},
		evolution: {
			title: "Time Evolution",
			subtitle: "Track the evolution of metrics over time",
			yAxisClients: "Clients",
			referenceAverage: "Average",
			clientsTooltip: "{{value}} clients",
			activeBase: {
				title: "Active Base Evolution",
				subtitle: "Unique clients per period",
			},
			averageTicket: {
				title: "Average Ticket Evolution",
				subtitle: "Average value per visit",
			},
			newVsRecurring: {
				title: "New vs. Recurring",
				subtitle: "Base composition over time",
				newClients: "New",
				recurringClients: "Recurring",
			},
			activation: {
				title: "Activation Rate Over Time",
				subtitle:
					"Conversion of new to recurring · 180-day rolling window · vs. same period last year",
				currentSeries: "Current",
				previousSeries: "Previous year",
			},
		},
		health: {
			title: "Base Health",
			subtitle: "Overview of the main customer indicators",
			variationTooltip: "Variation compared to the previous period",
			activeClients: {
				label: "Active Customers",
				subLabel: "Unique customers in the period",
				tooltip:
					"Customers identified (tax ID/phone) with at least one interaction in the selected period",
			},
			averageTicket: {
				label: "Average Ticket",
				subLabel: "Average value per visit",
			},
			activationRate: {
				label: "Activation Rate",
				subLabel: "Conversion of new into recurring",
				tooltip: "Percentage of new customers who returned for a second visit",
				variationSuffix: " p.p.",
			},
			reactivated: {
				label: "Reactivated Customers",
				subLabel: "Inactive customers who returned",
				tooltip: "Customers who were inactive (>30 days) and returned in the period",
			},
			interval: {
				label: "Average Interval",
				subLabel: "Days between visits",
				tooltip: "The lower, the better - indicates higher engagement",
				daysValue: "{{days}} days",
			},
		},
		rfmSection: {
			sectionTitle: "Customer Analysis",
			sectionSubtitle: "Distribution of the base by RFM classification",
			chartTitle: "RFM Distribution",
			chartSubtitle:
				"Distribution of the full base · click a segment to see details · sort by the columns",
		},
		rfmDistribution: {
			columns: {
				name: "Classification",
				percentage: "% of base",
				count: "Customers",
				averageTicket: "Average ticket",
				totalTpv: "TPV",
			},
			rowTitle: "View customers in this segment",
		},
		segmentsSection: {
			title: "Custom Segments",
			emptySubtitle: "Group customers by behavior and track their indicators",
			emptyTitle: "No custom segments found",
			emptyText:
				'Create segments to group customers by behavior — for example "VIPs" or "Lapsed with balance" — and track each group\'s numbers here on the dashboard.',
			createButton: "Create segment",
			subtitle: "Click a segment to see its customers",
			columnName: "Segment Name",
			columnCount: "Customers",
			columnPercentage: "% Base",
			columnAverageTicket: "Average Ticket",
			columnActions: "Actions",
			editSegment: "Edit segment",
			deleteSegment: "Delete segment",
		},
		mixClients: {
			title: "Client Mix",
			new: "New",
			recurring: "Recurring",
			reactivated: "Reactivated",
		},
		valueMatrix: {
			loading: "Loading…",
			noData: "No data in the period.",
			title: "Customer concentration",
			total: "Total: {{total}} customers",
			yAxisTitle: "average ticket (R$)",
			xAxisTitle: "average visits/month",
			cellTitle: "{{ticket}}  ·  {{frequency}} visits/month: {{clients}} customers",
		},
	},
	customers: {
		title: "Customer List",
		subtitle: "Search and filter customers from your database",
		globalFilters: {
			placeLabel: "Place",
			placePlaceholder: "All venues",
			searchPlaceholder: "Search...",
			noResults: "No results",
			baseLabel: "Considered base",
			baseOptions: {
				all: "Entire base",
				last90Days: "Last 90 days",
				last180Days: "Last 180 days",
				last365Days: "Last 365 days",
			},
		},
		relativeTime: {
			today: "today",
			daysAgo_one: "{{count}} day ago",
			daysAgo_other: "{{count}} days ago",
			monthsAgo_one: "{{count}} month ago",
			monthsAgo_other: "{{count}} months ago",
			yearsAgo_one: "{{count}} year ago",
			yearsAgo_other: "{{count}} years ago",
		},
		classificationView: {
			label: "Customer classification:",
			all: "All venues",
			best: "Best only",
		},
		filters: {
			removePill: "Remove {{label}}",
			search: {
				label: "Search",
				placeholder: "Search by name, email, phone or document...",
			},
			listSearch: {
				placeholder: "Search...",
				noResults: "No results",
			},
			place: {
				label: "Place",
				placeholder: "All places",
				selectedCount: "{{count}} selected",
			},
			rfmClassification: {
				label: "RFM Classification",
				placeholder: "All classifications",
				selectedCount: "{{count}} selected",
			},
			clientClassification: {
				label: "Multi-venue RFM classification",
				tooltip:
					"When a customer visited several venues and has different classifications, this filter defines which classification is displayed.",
				all: "Show all classifications",
				best: "Show best only",
			},
			segments: {
				label: "Segments",
				placeholder: "All segments",
				selectedCount: "{{count}} selected",
			},
			allowCommunication: {
				label: "Allows Communication",
				all: "All",
				yes: "Yes",
				no: "No",
			},
			status: {
				label: "Status",
				placeholder: "All statuses",
				selectedCount: "{{count}} selected",
			},
			gender: {
				label: "Gender",
				placeholder: "All genders",
				selectedCount: "{{count}} selected",
				male: "Male",
				female: "Female",
				other: "Other",
			},
			lastInteraction: {
				label: "Last Interaction",
				placeholder: "Any date",
				last7Days: "Last 7 days",
				last30Days: "Last 30 days",
				last60Days: "Last 60 days",
				last90Days: "Last 90 days",
				moreThan90Days: "More than 90 days",
				custom: "Custom",
			},
			activityPeriod: {
				label: "Activity Period",
				from: "From",
				to: "To",
				lastDays: "Last {{count}} days",
				shortcutsLabel: "Last:",
			},
			balance: {
				label: "Available Balance",
				onlyWithBalance: "Only with balance",
				minBalance: "Minimum balance ($)",
			},
			consumptionMin: {
				label: "Minimum consumption ($)",
				placeholder: "e.g. 100",
			},
			avgTicketMin: {
				label: "Minimum average ticket ($)",
				placeholder: "e.g. 50",
			},
			presences: {
				label: "Minimum visits",
				labelTotal: "Minimum visits (total)",
				any: "Any",
				option: "{{count}}+ visits",
				placeholder: "e.g. 3",
			},
			birthday: {
				label: "Birthday range",
				from: "From",
				to: "To",
				shortcutsLabel: "Suggestions:",
				thisMonth: "This month",
				nextMonth: "Next month",
				invalidDate: "Invalid date.",
				dayColumn: "Day",
				monthColumn: "Month",
			},
			birthYear: {
				label: "Age",
				modeAge: "Age",
				modeYear: "Year",
				yearLabel: "Birth year",
				from: "From",
				to: "To",
			},
			rangeError: "“To” must be greater than “From”.",
			product: {
				label: "Product",
				placeholder: "All products",
				selectedCount: "{{count}} selected",
			},
			sections: {
				activity: "Activity & spending",
				demographics: "Demographics",
			},
			advanced: {
				show: "More filters",
				hide: "Less filters",
				drawerButton: "Filters",
				drawerTitle: "List filters",
				drawerApply: "View {{value}} customers",
				sectionDemographics: "Demographics",
				sectionActivity: "Consumption",
				sectionStatus: "Status",
			},
			actions: {
				apply: "Apply Filters",
				clear: "Clear Filters",
			},
			results: "{{value}} customers found",
			searching: "Searching customers...",
			segmentMerge: {
				eyebrow: "Segment",
				hint: "Each selected segment's rules fill the filters below. Refine as you like.",
				appliedTitle: "Applied filters",
				appliedByOne: "from 1 segment",
				appliedByMany: "from {{count}} segments",
				editableBadge: "editable",
				markerApplied: "Segment applied",
				markerCombined: "Segment combined",
				markerAdjusted: "Segment adjusted",
				appliedFrom: "This filter comes from the {{segment}} segment.",
				appliedActivity:
					"Active in the last {{days}} days — set by the {{segments}} segment.",
				combinedFrom: "This filter unites the segments: {{segments}}.",
				combinedWithManual:
					"Unites the segments {{segments}} plus your manual selection.",
				conflictMsg:
					"More than one segment defines this filter. The last selected wins: {{used}} ({{usedValue}}).",
				activityAdjusted:
					"Segments use different windows. We use the largest — {{days}} days.",
			},
		},
		table: {
			columns: {
				name: "Customer",
				contact: "Contact",
				cpf: "Document",
				classification: "Classification",
				behavior: "Behavior",
				birthDate: "Birthday",
				firstVisit: "First visit",
				totalPaid: "Total paid",
				averageTicket: "Avg Ticket",
				balance: "Available balance",
				hasApp: "App active",
				actions: "Actions",
			},
			visits: "{{count}} visits",
			lastVisit: "Last visit",
			classificationPlace: "{{places}}",
			classificationPlaces: "{{places}}",
			uninformed: "Not informed",
			appActive: "Yes",
			appInactive: "No",
			hasAppTitle: "App active",
			balanceShort: "Balance",
			firstVisitShort: "First visit",
			lastVisitShort: "Last visit",
			noName: "No name",
			ago: "{{days}} days ago",
			totalPaid: "Total paid",
			averageTicket: "Average ticket",
			balance: "Balance",
			viewDetails: "View Details",
			details: "Details",
			noResults: "No customers found",
			noResultsDescription: "Try adjusting your filters to find customers",
		},
		status: {
			active: "Active",
			attention: "Attention",
			risk: "Risk",
			inactive: "Inactive",
		},
		drawer: {
			title: "Customer Details",
			noName: "No name",
			tabs: {
				summary: "Summary",
				favoriteProducts: "Favorite Products",
				history: "History",
				purchases: "Visits",
			},
			summary: {
				personalData: "Personal Data",
				document: "Document",
				email: "Email",
				phone: "Phone",
				birthDate: "Birth Date",
				ageYears: "{{age}} yrs",
				gender: "Gender",
				city: "City",
				location: "Location",
				preferences: "Preferences",
				allowsCommunication: "Allows Communication",
				communicationDenied: "No communication",
				appInstalled: "App installed",
				noApp: "No app",
				activitySummary: "Activity Summary",
				firstVisit: "First Visit",
				lastVisit: "Last Visit",
				totalVisits: "Total Visits",
				averageFrequency: "Average Frequency",
				visitsPerMonth: "visits/month",
				customerFor: "Days as customer",
				financial: "Financial",
				totalPaid: "Total Paid",
				averageTicket: "Average Ticket",
				availableBalance: "Available Balance",
				classifications: "Classifications",
				rfmByPlace: "RFM by Establishment",
				segments: "Segments",
			},
			products: {
				product: "Product",
				category: "Category",
				quantity: "Quantity",
				totalValue: "Total Value",
				lastPurchase: "Last Purchase",
				top10: "Top 10 Products",
				sortByQuantity: "By quantity",
				sortByValue: "By value",
				noProducts: "No favorite products found",
			},
			history: {
				filterByType: "Filter by type",
				allTypes: "All types",
				loadMore: "Load more",
				noHistory: "No history found",
				noPlace: "No establishment",
				unknownPlace: "Other establishment",
				interaction: "visit",
				interactions: "visits",
				firstShort: "1st:",
				lastShort: "last:",
				items: "items",
			},
			purchases: {
				product: "Product",
				category: "Category",
				quantity: "Qty",
				unitPrice: "Unit Price",
				total: "Total",
				date: "Date",
				noPurchases: "No purchases found",
				loadMore: "Load more",
				visitTotal: "Visit total",
				visitNoItems: "No recorded consumption",
				unitSuffix: "ea.",
				discount: "Discount",
				noPlace: "No establishment",
				unknownPlace: "Other establishment",
				loadingMore: "Loading more visits…",
			},
			actions: {
				close: "Close",
				addToSegment: "Add to Segment",
				edit: "Edit",
			},
		},
		genders: {
			male: "Male",
			female: "Female",
			other: "Other",
			unknown: "Not informed",
		},
		interactionTypes: {
			presence: "Presence",
			recharge: "Recharge",
			giftbackRescue: "Giftback Rescue",
			giftbackExpire: "Giftback Expiration",
			giftbackReceive: "Giftback Receive",
			ticketPurchase: "Ticket Purchase",
			ticketCancel: "Ticket Cancel",
			communication: "Communication",
			rfmMigration: "RFM Migration",
		},
		labels: {
			status: {
				active: "Active",
				attention: "Attention",
				risk: "At risk",
				inactive: "Inactive",
			},
			interaction: {
				Event: "Event",
				TicketOrder: "Ticket Order",
				NpsSurveyAnswer: "NPS Response",
				Communication: "Communication",
				SegmentAlteration: "Segment Change",
			},
		},
	},
	segments: {
		title: "Segments",
		subtitle: "Create and manage custom customer segments",
		summary: {
			highestRevenue: "Highest revenue segment",
			highestTicket: "Highest avg ticket segment",
			mostCustomers: "Segment with most customers",
			leastCustomers: "Segment with fewest customers",
		},
		search: {
			placeholder: "Search segment...",
		},
		actions: {
			create: "Create segment",
			edit: "Edit",
			delete: "Delete",
			duplicate: "Duplicate",
		},
		card: {
			createdBy: "Created by: {{name}}",
			leads: "{{count}} leads",
			metrics: {
				clients: "Customers",
				revenue: "Revenue",
				avgTicket: "Avg ticket",
				baseShare: "% of base",
			},
			lastSync: "Last sync: {{date}}",
			neverSynced: "Never synced",
			syncStatus: {
				synced: "Synced",
				outdated: "Outdated",
				outdatedTooltip:
					"The segment was modified after the last sync and needs to be reprocessed.",
				neverTooltip: "This segment has not been synced yet.",
			},
		},
		drawer: {
			titleCreate: "Create segment",
			titleEdit: "Edit segment",
			fields: {
				name: "Name",
				namePlaceholder: "Segment name",
				description: "Description",
				descriptionPlaceholder: "Describe the purpose of this segment",
				analysisPeriod: "Active in the last N days",
				analysisPeriodPlaceholder: "E.g.: 30 (empty = any)",
				analysisPeriodShortcuts: "Suggestions:",
			},
			rules: {
				title: "Segmentation Rules",
				demographic: "Demographic",
				consumption: "Consumption",
				engagement: "Engagement",
				status: "Status",
				gender: {
					label: "Gender",
					placeholder: "Select genders",
					male: "Male",
					female: "Female",
					other: "Other",
				},
				birthday: {
					label: "Birthday (day/month)",
					from: "From",
					to: "To",
					shortcutsLabel: "Suggestions:",
					thisMonth: "This month",
					nextMonth: "Next month",
					invalidDate: "Invalid date.",
				},
				birthYear: {
					label: "Age",
					modeAge: "Age",
					modeYear: "Year",
					yearLabel: "Birth year",
					from: "From",
					to: "To",
				},
				rangeError: "“To” must be greater than “From”.",
				listSearch: {
					placeholder: "Search...",
					noResults: "No results",
				},
				minPresences: {
					label: "Minimum visits",
					placeholder: "Minimum visits in period",
				},
				minTotalPaid: {
					label: "Minimum total value",
					placeholder: "$ 0.00",
					tooltip:
						"Minimum accumulated amount spent by the customer during the defined analysis period. Only customers with total spending equal to or above this value will be included in the segment.",
				},
				minAvgTicket: {
					label: "Minimum average ticket",
					placeholder: "$ 0.00",
				},
				minRemainingBalance: {
					label: "Minimum available balance",
					placeholder: "$ 0.00",
				},
				rfmClassification: {
					label: "RFM Classification",
					placeholder: "Select classifications",
				},
				allowCommunication: {
					label: "Allows communication",
					any: "Any",
					yes: "Yes",
					no: "No",
				},
			},
			actions: {
				save: "Save",
				cancel: "Cancel",
				saving: "Saving...",
			},
			validation: {
				nameRequired: "Name is required",
				descriptionRequired: "Description is required",
				analysisPeriodRequired: "Analysis period is required",
				analysisPeriodMin: "Period must be greater than 0",
			},
		},
		deleteModal: {
			title: "Delete segment",
			message:
				'Are you sure you want to delete the segment "{{name}}"? This action cannot be undone.',
			confirm: "Delete",
			cancel: "Cancel",
			deleting: "Deleting...",
		},
		empty: {
			title: "No segments found",
			description: "Create your first segment to start organizing your customers.",
			searchEmpty: "No segments match your search.",
		},
		toast: {
			createSuccess: "Segment created successfully",
			updateSuccess: "Segment updated successfully",
			deleteSuccess: "Segment deleted successfully",
		},
	},
	rfmAnalysis: {
		title: "RFM Analysis",
		subtitle: "Recency, Frequency and Monetary Value analysis of customers",
		configButton: "Configure parameters",
		rangesButton: "Classification rulers",
		countingNote:
			"Count per venue: a customer with different classifications across venues is counted in each one — the sum of the classes may exceed the number of unique customers.",
		viewModes: {
			simplified: "Simplified",
			detailed: "Detailed",
		},
		filters: {
			establishment: "Establishment",
			allEstablishments: "All establishments",
			period: "Period",
			groupBy: "Group by",
			periods: {
				last7Days: "Last 7 days",
				last15Days: "Last 15 days",
				last30Days: "Last 30 days",
				last60Days: "Last 60 days",
				last90Days: "Last 90 days",
			},
			groupByOptions: {
				week: "By week",
				month: "By month",
			},
		},
		charts: {
			distributionTitle: "RFM Status Count Over Time",
			distributionSubtitle: "Customer distribution evolution",
			donutTitle: "Customer Distribution",
			donutSubtitle: "Segmentation by RFM classification",
			treemapTitle: "RFM Distribution",
			treemapSubtitle: "Click a segment to see details",
			stackedAreaTitle: "Base Composition Over Time",
			stackedAreaSubtitle: "RFM segment evolution",
			total: "Total",
			clients: "customers",
			visible: "Visible",
			donutTooltip: "{{value}} customers ({{percentage}}%)",
		},
		table: {
			title: "RFM Classification Breakdown",
			subtitle: "Detailed metrics for each segment",
			columns: {
				classification: "Classification",
				clients: "Customers",
				percentBase: "% Base",
				totalTpv: "Total TPV",
				percentTpv: "% TPV",
				averageTicket: "Avg Ticket",
			},
			total: "Total",
			noData: "No data available for the selected period",
			rowTitle: "View customers in this segment",
			showInChart: "Click to show in the chart",
			hideFromChart: "Click to hide from the chart",
		},
		cards: {
			averageTicket: "Avg Ticket",
		},
		summaryCards: {
			groupTitle: "View customers in this group",
		},
		groups: {
			fieis: "Top customers",
			promissores: "Good customers",
			emRisco: "Customers on alert",
			perdidos: "Critical customers",
		},
		quintileRanges: {
			title: "Classification rulers",
			subtitle:
				"Recency, Frequency and Ticket ranges used to classify this venue's customers (quintiles calculated over the venue's own active base)",
			placePlaceholder: "Establishment",
			searchPlaceholder: "Search...",
			noResults: "No results",
			columnClassification: "Classification",
			columnRecency: "Recency (last visit)",
			columnFrequency: "Frequency (visits)",
			columnTicket: "Average ticket per visit",
			footnoteMain: "Ranges recalculated daily from the venue's own behavior",
			footnoteReference: " (reference: {{date}})",
			footnoteLegend:
				". “—” = range not reachable at this venue (little variation in the base).",
			ranges: {
				empty: "—",
				recencyAny: "any recency",
				recencyUpTo: "up to {{value}} days",
				recencyMoreThan: "more than {{value}} days",
				recencyExact: "{{value}} days",
				recencyBetween: "from {{min}} to {{max}} days",
				frequencyAny: "any number of visits",
				frequencySingle: "1 visit",
				frequencyUpTo: "up to {{value}} visits",
				frequencyAtLeast: "{{value}}+ visits",
				frequencyExact_one: "{{value}} visit",
				frequencyExact_other: "{{value}} visits",
				frequencyBetween: "from {{min}} to {{max}} visits",
				ticketAny: "any ticket",
				ticketUpTo: "up to {{value}}",
				ticketAbove: "above {{value}}",
				ticketBetween: "from {{lower}} to {{upper}}",
			},
		},
		treemap: {
			clients: "customers",
			ofBase: "of the base",
			averageTicketLabel: "Average ticket",
			noData: "No data in the period.",
		},
		configModal: {
			title: "Configure RFM Parameters",
			warning:
				"Changing RFM rules will recalculate the classification of all customers. This operation may take a few minutes to process.",
			resetButton: "Reset to Default",
			cancelButton: "Cancel",
			saveButton: "Save Configuration",
			savingButton: "Saving...",
		},
		errors: {
			loadTitle: "Error loading data",
			loadText: "Could not load RFM analysis data. Please try again.",
			retryButton: "Try again",
		},
		empty: {
			title: "No data to display",
			text:
				"There is not enough data to generate the RFM analysis for the selected period. Try selecting a longer period or wait for more customer interactions.",
		},
		toast: {
			saveSuccess: "RFM configuration saved successfully",
			saveError: "Error saving RFM configuration",
			resetSuccess: "RFM configuration reset to default",
		},
	},
};
